import { clampToInputBounds } from './clampToInputBounds';
import { findPanelResizeInput } from './findPanelResizeInput';

/** Sets the value via the native setter (bypassing React's value tracking) and dispatches `input` + `change` so React's synthetic `onChange` still fires. */
const setInputValueAndDispatch = (input: HTMLInputElement, value: number): void => {
	const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
		window.HTMLInputElement.prototype,
		'value',
	)?.set;

	if (nativeInputValueSetter) {
		nativeInputValueSetter.call(input, String(value));
	} else {
		input.value = String(value);
	}

	input.dispatchEvent(new Event('input', { bubbles: true }));
	input.dispatchEvent(new Event('change', { bubbles: true }));
};

/**
 * Resizes the enclosing Nav 4 panel to `targetWidth` (px), clamped to the panel's
 * min/max bounds. Returns `false` if the chat isn't inside a resizable panel.
 *
 * The splitter only recalculates the range input's `value`/`min`/`max` on focus,
 * and applies those updates asynchronously. So we focus first, then defer the
 * actual resize to the next frame — dispatching synchronously reads stale bounds
 * and no-ops on the first click.
 */
export const resizePanelTo = (fromElement: HTMLElement | null, targetWidth: number): boolean => {
	const input = findPanelResizeInput(fromElement);
	if (!input) {
		return false;
	}

	input.focus();

	const applyResize = () => {
		setInputValueAndDispatch(input, clampToInputBounds(input, targetWidth));
	};

	if (typeof requestAnimationFrame === 'function') {
		requestAnimationFrame(applyResize);
	} else {
		applyResize();
	}

	return true;
};
